import { capitalize, uniqueId } from "lodash";
import moment from "moment";

import { hasItems } from "../../../helpers/utils";

export const isNewUser = (joinDateTime, interval = 24) => {
  const now = moment();
  const userJoinedTime = moment(joinDateTime);

  // Joined in the last {interval} hours
  return now.diff(userJoinedTime, "hours") < interval;
};

export const getUserCreatedTime = (datetimeCreated, debugHours = 0) => {
  return debugHours > 0
    ? moment().add(-debugHours, "hours").format("YYYY-MM-DD hh:mm a")
    : datetimeCreated;
};

export const getAllUsers = (controller) => {
  const users = {};
  (controller.state.teams || []).forEach((team) => {
    (team.members || []).forEach((m) => {
      users[m.email] = m;
    });
  });

  return users;
};

export const getShareUsers = (controller) => {
  let users = [];

  if (!controller || !controller.state) return users;

  (controller.state?.teams || []).forEach((team) => {
    (team?.members || []).forEach((m) => {
      users.push({
        ...m,
        name: m.name || m.email,
        companyInfo: team.name + " | " + capitalize(m.role),
      });
    });
  });

  return users;
};

export const getUserTeams = (controller) => {
  let teams = {};
  if (!controller || !controller.state) return teams;

  (controller.state?.teams || []).forEach((team) => {
    (team?.members || []).forEach((m) => {
      if (m.email) teams[m.email] = team.name;
    });
  });

  return teams;
};

export const getManagers = (controller) => {
  let users = [];

  if (!controller || !controller.state) return users;

  (controller.state?.teams || []).forEach((team) => {
    (team?.members || []).forEach((m) => {
      if (m.role == "manager" || m.role == "coach") {
        users.push({
          ...m,
          name: m.name || cleanupEmail(m.email),
          companyInfo: team.name + " | " + capitalize(m.role),
        });
      }
    });
  });

  return users;
};

export const cleanupEmail = (email) => {
  return email ? email.split("@")[0] : "";
};

export const getUsersHeaderText = (recipients, prefix = "") => {
  if (!hasItems(recipients)) return "";

  const users = Array.isArray(recipients)
    ? recipients.map((r) => r.name || cleanupEmail(r.email))
    : [recipients?.name];

  return (
    prefix + (users[0] + (users.length > 1 ? ` +${users.length - 1}` : ""))
  );
};

export const filterUniqueOtherUsers = (controller, userList) => {
  const aliases = [];
  aliases.push(controller.state.impersonate || controller.state.currentUser);
  (controller.state.teams || []).forEach((team) => {
    if (team.email_alias) aliases.push(team.email_alias);
  });

  return userList != null
    ? userList.filter(
        (e, i, self) =>
          !aliases.includes(e.email) &&
          self.findIndex((e2) => e.email === e2.email) === i,
      )
    : null;
};

export const getAliasForReport = (controller, userList) => {
  let user = controller.state.impersonate || controller.state.currentUser;

  (controller.state.teams || []).forEach((team) => {
    let inTeam = false;
    (userList || []).forEach((u) => {
      team.members.forEach((m) => {
        if (m.email === u.email) inTeam = true;
      });
    });
    if (inTeam && team.email_alias) user = team.email_alias;
  });

  return user;
};

export const computeReportReceivers = (reports, type) => {
  if (!reports) return [];

  reports.forEach((report) => {
    let users = [
      {
        name: report.user_nickname,
        email: report.email_user,
      },
    ];

    // Sent reports
    if (type === "sent") {
      const nicknames = Array.isArray(report.receiver_nickname)
        ? report.receiver_nickname
        : [report.receiver_nickname];

      const receivers = Array.isArray(report.receiver)
        ? report.receiver
        : [report.receiver];

      if (hasItems(nicknames)) {
        users = [];
        for (let index = 0; index < nicknames.length; index++) {
          const name = nicknames[index];
          const email = receivers[index];
          if (name || email) {
            users.push({ name: name || cleanupEmail(email), email });
          }
        }
      }
    }

    report.users = users;
  });

  return reports;
};

export const groupReportsByTeam = (reports, controller) => {
  const userTeams = getUserTeams(controller);
  let teamReports = {};

  reports.forEach((report) => {
    const user = report.receiver;
    const dateId = moment(report.reference_day).unix();

    // Group by team + date + timeUnit
    const key = [userTeams[user] || uniqueId(), dateId, report.time_unit].join(
      "_",
    );

    if (!teamReports[key]) {
      teamReports[key] = {
        isTeamReport: true,
        reports: [],
        minutes: 0,
        mood: -1,
      };
    }
    if (report.minutes) {
      teamReports[key].minutes += 1 * report.minutes;
    }
    if (report.mood > teamReports[key].mood) {
      teamReports[key].mood = report.mood;
    }

    teamReports[key].reference_day = report.reference_day;
    teamReports[key].id_aggregated_report = report.id_aggregated_report;
    teamReports[key].timeUnit = report.time_unit;
    teamReports[key].reports.push(report);
  });

  let reportsList = [];
  Object.values(teamReports).forEach((r) => {
    if (r.reports?.length > 1) {
      reportsList.push(r);
    } else {
      reportsList.push(r.reports[0]);
    }
  });

  return reportsList;
};

const UsersHelper = {
  getManagers,
  getUsersHeaderText,
  getShareUsers,
  filterUniqueOtherUsers,
  getAliasForReport,
  cleanupEmail,
  getUserTeams,
  getAllUsers,
  computeReportReceivers,
  groupReportsByTeam,
  isNewUser,
  getUserCreatedTime,
};

export default UsersHelper;
